import { supabase } from '../lib/supabase';
import { OrganizationService } from './organization.service';
import {
  Customer,
  CustomerVehicleInterest,
  CustomerCommunication,
  TestDrive,
  CustomerDocument,
  CustomerStats,
  LeadStatus,
  LeadSource,
  CommunicationType,
  SimplifiedLeadStage,
  LeadTemperature
} from '../types/customer';
import {
  parseNameQuery,
  normalizePhoneNumber,
  normalizeQuery
} from '../utils/search-utils';

// Helper function to convert flat address fields to nested Address object
function mapFlatToNestedAddress(customer: any) {
  const { address_line1, address_line2, suburb, state, postcode, ...rest } = customer;
  
  // Only create address object if at least one field exists
  const hasAddress = address_line1 || address_line2 || suburb || state || postcode;
  
  return {
    ...rest,
    address: hasAddress ? {
      street: [address_line1, address_line2].filter(Boolean).join(' ').trim() || undefined,
      suburb: suburb || undefined,
      state: state || undefined,
      postcode: postcode || undefined,
      country: 'Australia'
    } : undefined
  };
}

// Helper function to convert nested Address object to flat fields
function mapNestedToFlatAddress(customer: any) {
  const { address, ...rest } = customer;
  
  if (!address) {
    return rest;
  }
  
  // Split street into line1 and line2 if possible
  const streetParts = (address.street || '').split(',').map((s: string) => s.trim());
  
  return {
    ...rest,
    address_line1: streetParts[0] || '',
    address_line2: streetParts[1] || '',
    suburb: address.suburb || '',
    state: address.state || '',
    postcode: address.postcode || ''
  };
}

export interface CustomerFilters {
  search?: string;
  lead_status?: LeadStatus | 'all';
  lead_stage?: SimplifiedLeadStage | 'all';
  lead_temperature?: LeadTemperature | 'all';
  lead_source?: LeadSource | 'all';
  assigned_to?: string | 'all';
  date_from?: string;
  date_to?: string;
  /** Exclude suppliers/dealers/finance companies. Use on the Leads page. */
  exclude_business_types?: boolean;
  /** Limit business contacts to a specific type. */
  contact_type?: 'customer' | 'supplier' | 'dealer' | 'finance_company';
}

class CustomerService {
  // Get all customers with filters
  async getCustomers(filters?: CustomerFilters) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Build query
      let query = supabase
        .from('customers')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        const searchQuery = filters.search.trim(); // ALWAYS trim spaces!
        if (!searchQuery) return { data: [], error: null }; // Empty search returns nothing

        const searchTerms: string[] = [];

        // Split into words for multi-word searches (e.g., "John Smith")
        const words = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);

        // Search each word in first name and last name
        words.forEach(word => {
          searchTerms.push(`first_name.ilike.%${word}%`);
          searchTerms.push(`last_name.ilike.%${word}%`);
        });

        // Also search with full query (for exact matches)
        searchTerms.push(`first_name.ilike.%${searchQuery}%`);
        searchTerms.push(`last_name.ilike.%${searchQuery}%`);
        searchTerms.push(`email.ilike.%${searchQuery}%`);

        // Phone search: strip formatting and search
        const phoneDigits = normalizePhoneNumber(searchQuery);
        if (phoneDigits.length >= 4) {
          searchTerms.push(`phone.ilike.%${phoneDigits}%`);
          searchTerms.push(`mobile.ilike.%${phoneDigits}%`);
        }
        // Also search with original format
        searchTerms.push(`phone.ilike.%${searchQuery}%`);
        searchTerms.push(`mobile.ilike.%${searchQuery}%`);

        query = query.or(searchTerms.join(','));
      }

      if (filters?.lead_status && filters.lead_status !== 'all') {
        query = query.eq('lead_status', filters.lead_status);
      }

      // Note: lead_stage is a virtual field mapped from lead_status, not a database column
      // Filtering by lead_stage is handled client-side in the Customers page

      // Filter by lead temperature
      if (filters?.lead_temperature && filters.lead_temperature !== 'all') {
        query = query.eq('lead_temperature', filters.lead_temperature);
      }

      if (filters?.lead_source && filters.lead_source !== 'all') {
        query = query.eq('lead_source', filters.lead_source);
      }

      if (filters?.assigned_to && filters.assigned_to !== 'all') {
        query = query.eq('assigned_to', filters.assigned_to);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      // Filter out business contacts (suppliers, dealers, finance companies) for the Leads page
      if (filters?.exclude_business_types) {
        query = query
          .or('is_supplier.is.null,is_supplier.eq.false')
          .or('is_dealer.is.null,is_dealer.eq.false')
          .or('is_finance_company.is.null,is_finance_company.eq.false');
      }

      if (filters?.contact_type === 'supplier') query = query.eq('is_supplier', true);
      if (filters?.contact_type === 'dealer') query = query.eq('is_dealer', true);
      if (filters?.contact_type === 'finance_company') query = query.eq('is_finance_company', true);

      // Paginate through all results (PostgREST caps at 1000)
      const PAGE_SIZE = 1000;
      let allData: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);
        if (error) return { data: null, error };
        if (!data || data.length === 0) break;
        allData = allData.concat(data);
        hasMore = data.length === PAGE_SIZE;
        offset += PAGE_SIZE;
      }

      // Add computed fields
      const customers = allData.map(customer => ({
        ...customer,
        full_name: `${customer.first_name} ${customer.last_name}`,
        days_in_pipeline: Math.floor(
          (new Date().getTime() - new Date(customer.created_at).getTime()) / (1000 * 60 * 60 * 24)
        )
      }));

      return { data: customers, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Get single customer
  async getCustomer(customerId: string) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (data) {
        // Don't convert - data is already flat
        data.full_name = `${data.first_name} ${data.last_name}`;
        data.days_in_pipeline = Math.floor(
          (new Date().getTime() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24)
        );
        return { data, error };
      }

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Create customer
  async createCustomer(customer: Partial<Customer>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Validate minimum required fields
      if (!customer.first_name?.trim()) {
        throw new Error('First name is required');
      }
      if (!customer.last_name?.trim()) {
        throw new Error('Last name is required');
      }
      if (!customer.phone?.trim() && !customer.mobile?.trim()) {
        throw new Error('At least one phone number (Phone or Mobile) is required');
      }

      // Remove fields that don't exist in database
      const {
        interested_vehicle, // This doesn't exist in DB - handled separately
        potential_value, // This doesn't exist in DB - handled separately
        full_name, // This is computed in DB
        days_in_pipeline, // This is computed
        ...cleanCustomer
      } = customer as any;

      // Create minimal customer data with only required + provided fields
      const customerData: any = {
        // Required fields
        organization_id: organizationId,
        first_name: cleanCustomer.first_name.trim(),
        last_name: cleanCustomer.last_name.trim(),

        // Default values for non-null fields
        lead_status: cleanCustomer.lead_status || 'new',
        lead_source: cleanCustomer.lead_source || 'walk_in',
        marketing_consent: cleanCustomer.marketing_consent || false,
        created_by: user.id,

        // Include email only if provided (optional)
        ...(cleanCustomer.email?.trim() && { email: cleanCustomer.email.trim() }),

        // Include phone numbers (at least one is required)
        ...(cleanCustomer.phone?.trim() && { phone: cleanCustomer.phone.trim() }),
        ...(cleanCustomer.mobile?.trim() && { mobile: cleanCustomer.mobile.trim() }),
        ...(cleanCustomer.date_of_birth && { date_of_birth: cleanCustomer.date_of_birth }),

        // Address fields - only include if provided
        ...(cleanCustomer.address_line1?.trim() && { address_line1: cleanCustomer.address_line1.trim() }),
        ...(cleanCustomer.address_line2?.trim() && { address_line2: cleanCustomer.address_line2.trim() }),
        ...(cleanCustomer.suburb?.trim() && { suburb: cleanCustomer.suburb.trim() }),
        ...(cleanCustomer.state?.trim() && { state: cleanCustomer.state.trim() }),
        ...(cleanCustomer.postcode?.trim() && { postcode: cleanCustomer.postcode.trim() }),
        ...(cleanCustomer.country?.trim() && { country: cleanCustomer.country.trim() }),

        // Budget fields - only include if provided
        ...(cleanCustomer.budget_min !== undefined && cleanCustomer.budget_min !== null && { budget_min: cleanCustomer.budget_min }),
        ...(cleanCustomer.budget_max !== undefined && cleanCustomer.budget_max !== null && { budget_max: cleanCustomer.budget_max }),

        // Other optional fields
        ...(cleanCustomer.preferred_contact_method?.trim() && { preferred_contact_method: cleanCustomer.preferred_contact_method.trim() }),
        ...(cleanCustomer.notes?.trim() && { notes: cleanCustomer.notes.trim() }),
        ...(cleanCustomer.assigned_to && { assigned_to: cleanCustomer.assigned_to }),
      };

      console.log('Attempting to insert customer with minimal data:', customerData);

      const { data, error } = await supabase
        .from('customers')
        .insert(customerData)
        .select()
        .single();

      if (error) {
        console.error('Customer insert error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          customerData
        });

        // Provide more specific error messages
        if (error.message?.includes('duplicate') || error.code === '23505') {
          if (error.message?.includes('email')) {
            throw new Error('A customer with this email address already exists');
          } else if (error.message?.includes('phone')) {
            throw new Error('A customer with this phone number already exists');
          } else {
            throw new Error('This customer already exists in the system');
          }
        } else if (error.message?.includes('organization_id') || error.code === '23503') {
          throw new Error('Invalid organization. Please contact support');
        } else if (error.message?.includes('not-null') || error.code === '23502') {
          throw new Error('Missing required information. Please check that first name and last name are provided');
        } else {
          // Generic error for other database issues
          throw new Error(`Unable to create customer: ${error.message}`);
        }
      } else {
        console.log('Customer created successfully:', data);
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Customer creation error:', error);
      return { data: null, error };
    }
  }

  // Update customer
  async updateCustomer(customerId: string, updates: Partial<Customer>) {
    try {
      // Don't map to nested structure - keep flat fields for database
      // Remove the address field if it exists
      const { address, ...flatUpdates } = updates as any;

      const { data, error } = await supabase
        .from('customers')
        .update(flatUpdates)
        .eq('id', customerId)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Delete customer
  async deleteCustomer(customerId: string) {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  // Get customer documents
  async getCustomerDocuments(customerId: string) {
    try {
      const { data, error } = await supabase
        .from('customer_documents')
        .select('*')
        .eq('customer_id', customerId)
        .order('generated_at', { ascending: false });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Update lead status
  async updateLeadStatus(customerId: string, status: LeadStatus) {
    try {
      console.log(`[DEBUG customers.service] Updating lead status for customer ${customerId} to: ${status}`);

      // Map status to stage to keep them in sync
      const { mapLeadStatusToStage, getStageLabel } = await import('../utils/leadStageMapping');
      const stage = mapLeadStatusToStage(status);
      const stageLabel = getStageLabel(stage);
      console.log(`[DEBUG customers.service] Mapped status ${status} to stage ${stage} (${stageLabel})`);

      // Update lead_status (lead_stage is a virtual field mapped from lead_status)
      const result = await this.updateCustomer(customerId, {
        lead_status: status
      });
      console.log(`[DEBUG customers.service] Update result:`, result);

      if (!result.error) {
        // Add a communication record for the status change with NEW simplified stage messages
        const stageMessages: Record<string, string> = {
          'new_lead': 'Lead created - initial contact established',
          'test_drive': 'Test drive scheduled - customer evaluating vehicle',
          'proposal': 'Proposal stage - reviewing pricing and options',
          'won': 'Deal won - customer has committed to purchase',
          'lost': 'Deal lost - customer will not proceed'
        };

        await this.addCommunication({
          customer_id: customerId,
          communication_type: 'note',
          subject: `Lead Stage Updated to ${stageLabel}`,
          content: stageMessages[stage] || `Lead stage updated to ${stageLabel}`
        });
      }

      return result;
    } catch (error: any) {
      console.error(`[DEBUG customers.service] Error updating lead status:`, error);
      return { data: null, error };
    }
  }

  // Get customer statistics
  async getCustomerStats() {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .rpc('get_customer_stats', { org_id: organizationId });

      return { data: data?.[0] as CustomerStats, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Vehicle interests
  async getCustomerInterests(customerId: string) {
    try {
      const { data, error } = await supabase
        .from('customer_vehicle_interests')
        .select(`
          *,
          vehicle:vehicles(*)
        `)
        .eq('customer_id', customerId)
        .order('priority', { ascending: false });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async addVehicleInterest(interest: Partial<CustomerVehicleInterest>) {
    try {
      const { data, error } = await supabase
        .from('customer_vehicle_interests')
        .insert(interest)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async removeVehicleInterest(interestId: string) {
    try {
      const { error } = await supabase
        .from('customer_vehicle_interests')
        .delete()
        .eq('id', interestId);

      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  // Communications
  async getCustomerCommunications(customerId: string) {
    try {
      const { data, error } = await supabase
        .from('customer_communications')
        .select(`
          *,
          vehicle:vehicles(id, make, model, year)
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) return { data: null, error };

      // Enrich with user details from the users table
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(comm => comm.user_id).filter(Boolean))];

        if (userIds.length > 0) {
          const { data: users } = await supabase
            .from('users')
            .select('id, email, role')
            .in('id', userIds);

          // Also try to get profile info if it exists
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .in('id', userIds);

          // Create maps for quick lookup
          const userMap = new Map();
          const profileMap = new Map();

          users?.forEach(user => {
            userMap.set(user.id, user);
          });

          profiles?.forEach(profile => {
            profileMap.set(profile.id, profile);
          });

          // Enrich communications with user details
          const enrichedData = data.map(comm => {
            const user = userMap.get(comm.user_id);
            const profile = profileMap.get(comm.user_id);

            return {
              ...comm,
              created_by: {
                id: comm.user_id,
                email: user?.email || 'Unknown',
                first_name: profile?.first_name || null,
                last_name: profile?.last_name || null,
                role: user?.role || null
              }
            };
          });

          return { data: enrichedData, error: null };
        }
      }

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async addCommunication(communication: Partial<CustomerCommunication>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('customer_communications')
        .insert({
          ...communication,
          user_id: user.id
        })
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Test drives
  async getTestDrives(filters?: { customerId?: string; vehicleId?: string; date?: string }) {
    try {
      let query = supabase
        .from('test_drives')
        .select(`
          *,
          customer:customers(*),
          vehicle:vehicles(*)
        `)
        .order('scheduled_date', { ascending: true });

      if (filters?.customerId) {
        query = query.eq('customer_id', filters.customerId);
      }

      if (filters?.vehicleId) {
        query = query.eq('vehicle_id', filters.vehicleId);
      }

      if (filters?.date) {
        const startOfDay = new Date(filters.date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(filters.date);
        endOfDay.setHours(23, 59, 59, 999);

        query = query
          .gte('scheduled_date', startOfDay.toISOString())
          .lte('scheduled_date', endOfDay.toISOString());
      }

      const { data, error } = await query;

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async scheduleTestDrive(testDrive: Partial<TestDrive>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .from('test_drives')
        .insert({
          ...testDrive,
          organization_id: organizationId,
          scheduled_by: user.id
        })
        .select()
        .single();

      // Also add a communication record
      if (data) {
        await this.addCommunication({
          customer_id: testDrive.customer_id!,
          communication_type: 'test_drive',
          subject: 'Test Drive Scheduled',
          content: `Test drive scheduled for ${new Date(testDrive.scheduled_date!).toLocaleString()}`,
          vehicle_id: testDrive.vehicle_id,
          scheduled_at: testDrive.scheduled_date
        });
      }

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async updateTestDrive(testDriveId: string, updates: Partial<TestDrive>) {
    try {
      const { data, error } = await supabase
        .from('test_drives')
        .update(updates)
        .eq('id', testDriveId)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Contact inquiries management
  async getContactInquiries(status?: string) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      let query = supabase
        .from('contact_inquiries')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async convertInquiryToCustomer(inquiryId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the inquiry
      const { data: inquiry, error: inquiryError } = await supabase
        .from('contact_inquiries')
        .select('*')
        .eq('id', inquiryId)
        .single();

      if (inquiryError || !inquiry) {
        throw new Error('Inquiry not found');
      }

      // Check if already converted
      if (inquiry.converted_to_customer_id) {
        return { data: { customer_id: inquiry.converted_to_customer_id }, error: null };
      }

      // Create customer from inquiry
      const customerData = {
        first_name: inquiry.first_name,
        last_name: inquiry.last_name,
        email: inquiry.email,
        phone: inquiry.phone,
        preferred_contact_method: inquiry.preferred_contact_method || 'email',
        lead_source: 'website' as LeadSource,
        lead_status: 'new' as LeadStatus,
        notes: `Converted from website inquiry: ${inquiry.subject}\n\n${inquiry.message}`,
        created_by: user.id
      };

      const { data: customer, error: customerError } = await this.createCustomer(customerData);

      if (customerError || !customer) {
        throw customerError || new Error('Failed to create customer');
      }

      // Update inquiry with conversion info
      const { error: updateError } = await supabase
        .from('contact_inquiries')
        .update({
          converted_to_customer_id: customer.id,
          status: 'converted',
          processed_at: new Date().toISOString()
        })
        .eq('id', inquiryId);

      if (updateError) {
        console.error('Error updating inquiry:', updateError);
      }

      // Add initial communication record
      await this.addCommunication({
        customer_id: customer.id,
        communication_type: 'note',
        subject: 'Customer created from website inquiry',
        content: `Original inquiry: ${inquiry.subject}\n\n${inquiry.message}`
      });

      return { data: { customer_id: customer.id, customer }, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Match customers to vehicles
  async findMatchingCustomers(vehicle: any) {
    try {
      const { data: interests } = await supabase
        .from('customer_vehicle_interests')
        .select(`
          *,
          customer:customers(*)
        `)
        .or(`vehicle_id.eq.${vehicle.id},and(make.ilike.%${vehicle.make}%,model.ilike.%${vehicle.model}%)`);

      // Filter by criteria
      const matches = interests?.filter(interest => {
        if (interest.vehicle_id === vehicle.id) return true;

        let match = true;
        if (interest.year_min && vehicle.year < interest.year_min) match = false;
        if (interest.year_max && vehicle.year > interest.year_max) match = false;
        if (interest.body_type && vehicle.body_type !== interest.body_type) match = false;
        if (interest.fuel_type && vehicle.fuel_type !== interest.fuel_type) match = false;
        if (interest.transmission && vehicle.transmission !== interest.transmission) match = false;

        return match;
      });

      return { data: matches, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }
}

export const customerService = new CustomerService();